/**
 * THE REQUIREMENTS DOCUMENT AS A FILE SOMEBODY SIGNS — documents programme,
 * increment 2b.
 *
 * WHY A PDF. Requirements was, until now, the one generated document with no way
 * to become a file: the reviewer's document assembled live on screen, and the only
 * route to a signed copy was to upload one by hand — which threw away the assembly
 * the app already does and let the signed requirements drift from the cards the
 * team builds. This produces the document the screen shows, as a fixed file the
 * sign-off engine can fingerprint, exactly as the change request and the charter
 * are produced (#200).
 *
 * WHAT IT IS SHAPED LIKE. The same professional page the change request draws — a
 * logo above the title, numbered sections each opening with a filled band, a
 * summary table, bordered tables — so the two documents a project sends out read
 * as one product. The parts unique to requirements are the priority (MoSCoW)
 * marker beside each line, the roll-up table, and the four rich-text fields carried
 * over from About (scope, assumptions, constraints), which are flattened to their
 * text — bullets kept — because a signable document is plain Helvetica and does not
 * need the editor's full formatting to be read and approved.
 *
 * TAKES WHAT IT PRINTS. The caller composes the whole document — the backlog-derived
 * lines and the About narrative — into one RequirementsDoc (see composeRequirements)
 * and hands it here with a few facts (title, version, the date). This lays it out and
 * reaches for nothing, so the harder half can be tested without a PDF reader and the
 * same document always produces the same bytes.
 *
 * PLAIN HELVETICA, NO EMBEDDED FONT — one of the fourteen faces every reader has, so
 * the file stays small and opens the same everywhere, the same choice the change
 * request makes and for the same reason.
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import type { JSONContent } from '@tiptap/core';
import { winAnsi, unwrap } from './pdf-text';
import {
  drawLetterhead,
  drawBand,
  wordedFromSlashed,
  preparedSegments,
  revisionLabel,
  TINT_LABEL,
  BRAND,
  type Letterhead,
} from './pdf-chrome';
import { embedTitleFont } from './pdf-fonts';
import { MOSCOW_ORDER, MOSCOW_SHORT } from './types';
import type { RequirementsDoc } from './requirements';
import { richHasText } from './rich-text';

/** A4 in points, which is what pdf-lib measures in. */
const PAGE = { width: 595.28, height: 841.89 };
const MARGIN = 52;
const WIDTH = PAGE.width - MARGIN * 2;
const FOOT = 38;
const HEAD = 36;

const INK = rgb(0.1, 0.13, 0.16);
const QUIET = rgb(0.42, 0.46, 0.5);
const RULE = rgb(0.8, 0.83, 0.85);
const SUNK = rgb(0.965, 0.972, 0.976);

/** The customer's mark, passed in as bytes so this file reaches for nothing. */
export interface Logo {
  bytes: ArrayBuffer | Uint8Array;
  type: 'png' | 'jpg';
}

export interface RequirementsFacts {
  title: string;
  board: string;
  /** Stamped in, never read from the clock here, so the same document twice is the
   *  same bytes and a test can say what the date will be. */
  preparedOn: string;
  /** Who produced it — the signed-in person, named on the letterhead just before
   *  the prepared-on date. Stamped in at produce time, like the date. */
  preparedBy?: string;
  version: string;
  /** The document's standing in words — "Draft", "Awaiting sign-off", "Signed off". */
  status: string;
  logo?: Logo;
}

/** CPFC write 07/07/2026. Day first, which is what the Caribbean and the UK read. */
export const asDate = (at: number): string => {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * Words that fit, measured in the font they will be drawn in — wrapping by
 * counting characters is what makes a generated document look generated. A word
 * longer than the whole measure overhangs rather than being dropped.
 */
function wrap(text: string, font: PDFFont, size: number, measure: number): string[] {
  const lines: string[] = [];
  for (const paragraph of unwrap(winAnsi(text)).split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const tried = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(tried, size) <= measure) line = tried;
      else {
        if (line) lines.push(line);
        line = word;
      }
    }
    lines.push(line);
  }
  return lines;
}

/** What pdf-lib hands back from embedPng/embedJpg. */
type Art = Parameters<PDFPage['drawImage']>[0] & { width: number; height: number };
interface Embedded {
  art: Art;
  width: number;
  height: number;
}
interface Running {
  title: string;
  logo?: Embedded;
}

/** A logo that will not embed is skipped, not fatal — the document is the point. */
async function embed(pdf: PDFDocument, logo?: Logo): Promise<Embedded | undefined> {
  if (!logo) return undefined;
  try {
    const art =
      logo.type === 'png' ? await pdf.embedPng(logo.bytes) : await pdf.embedJpg(logo.bytes);
    return { art, width: art.width, height: art.height };
  } catch {
    return undefined;
  }
}

/* ── FLATTENING A RICH-TEXT FIELD TO LINES ─────────────────────────────────────
 *
 * The scope, assumptions and constraints fields are ProseMirror documents (rich
 * text, migration 0113). A signable PDF is plain Helvetica, so this walks the
 * document and returns each block as a line, keeping a bullet or number marker so a
 * list still reads as a list. Nested content is flattened rather than dropped —
 * losing a line from a signed document is worse than losing its indentation.
 */
interface Line {
  text: string;
  marker?: string;
  indent?: number;
}

/** The inline text of one block — text nodes joined, a hard break becoming a space. */
function inline(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return ' ';
  return (node.content ?? []).map(inline).join('');
}

function flatten(node: JSONContent, depth: number, out: Line[]): void {
  switch (node.type) {
    case 'paragraph': {
      const text = inline(node).trim();
      if (text) out.push({ text, indent: depth * 14 });
      break;
    }
    case 'heading': {
      const text = inline(node).trim();
      if (text) out.push({ text, indent: depth * 14 });
      break;
    }
    case 'bulletList':
      for (const li of node.content ?? []) listItem(li, depth, '•', out);
      break;
    case 'orderedList': {
      let n = (node.attrs?.start as number) ?? 1;
      for (const li of node.content ?? []) listItem(li, depth, `${n++}.`, out);
      break;
    }
    case 'blockquote':
      for (const child of node.content ?? []) flatten(child, depth + 1, out);
      break;
    default:
      for (const child of node.content ?? []) flatten(child, depth, out);
  }
}

function listItem(li: JSONContent, depth: number, marker: string, out: Line[]): void {
  const kids = li.content ?? [];
  kids.forEach((child, i) => {
    if (i === 0 && (child.type === 'paragraph' || child.type === 'heading')) {
      const text = inline(child).trim();
      out.push({ text: text || ' ', marker, indent: depth * 14 + 14 });
    } else {
      flatten(child, depth + 1, out);
    }
  });
}

function richLines(doc?: JSONContent | null): Line[] {
  const out: Line[] = [];
  for (const node of doc?.content ?? []) flatten(node, 0, out);
  return out;
}

/**
 * A pen that knows where it is on the page and starts a new one when it runs out —
 * the same closure the change request draws with, so the two documents share a look.
 */
function makePen(pdf: PDFDocument, regular: PDFFont, bold: PDFFont, running?: Running, titleFont?: PDFFont) {
  let page: PDFPage = pdf.addPage([PAGE.width, PAGE.height]);
  let y = PAGE.height - MARGIN;
  let section = 0;

  const carryOver = () => {
    if (!running) return;
    const top = y;
    const tall = 13;
    let x = MARGIN;
    if (running.logo) {
      const wide = (running.logo.width / running.logo.height) * tall;
      const scale = Math.min(1, WIDTH / 3 / wide);
      page.drawImage(running.logo.art, { x, y: top - tall * scale, width: wide * scale, height: tall * scale });
      x += wide * scale + 9;
    }
    page.drawText(winAnsi(running.title), { x, y: top - tall + 3.5, size: 8.5, font: regular, color: QUIET });
    page.drawLine({
      start: { x: MARGIN, y: top - tall - 9 },
      end: { x: MARGIN + WIDTH, y: top - tall - 9 },
      thickness: 0.75,
      color: RULE,
    });
    y = top - HEAD;
  };

  const room = (needed: number) => {
    if (y - needed >= MARGIN + FOOT) return;
    page = pdf.addPage([PAGE.width, PAGE.height]);
    y = PAGE.height - MARGIN;
    carryOver();
  };

  const api = {
    get pages() {
      return pdf.getPageCount();
    },
    down: (by: number) => {
      y -= by;
    },
    room,

    image(width: number, height: number, art: Parameters<PDFPage['drawImage']>[0]) {
      room(height + 10);
      y -= height;
      page.drawImage(art, { x: MARGIN, y, width, height });
      y -= 10;
    },

    text(
      body: string,
      opts: {
        size?: number;
        bold?: boolean;
        colour?: typeof INK;
        indent?: number;
        gap?: number;
        marker?: string;
      } = {}
    ) {
      const size = opts.size ?? 10;
      const font = opts.bold ? bold : regular;
      const indent = opts.indent ?? 0;
      const leading = size * 1.5;
      const lines = wrap(body, font, size, WIDTH - indent);
      lines.forEach((line, at) => {
        room(leading);
        y -= leading;
        if (at === 0 && opts.marker) {
          page.drawText(winAnsi(opts.marker), { x: MARGIN + indent - 14, y, size, font: regular, color: QUIET });
        }
        page.drawText(line, { x: MARGIN + indent, y, size, font, color: opts.colour ?? INK });
      });
      if (opts.gap) y -= opts.gap;
    },

    /** A numbered, tinted section band (R18) — numbered as it is drawn. */
    band(title: string) {
      const height = 26;
      room(height + 30);
      y -= height + 8;
      section += 1;
      drawBand(page, { x: MARGIN, yBottom: y, width: WIDTH, title: winAnsi(title), number: section, bold });
      y -= 10;
    },

    /** The letterhead (R18): eyebrow, big subject, quiet meta, logo, teal hairline. */
    letterhead(content: Letterhead) {
      y = drawLetterhead(page, { x: MARGIN, width: WIDTH, top: PAGE.height - MARGIN }, { regular, bold, title: titleFont }, content);
    },

    /**
     * A bordered table; row height is the tallest cell, and a row is never split.
     * `labels` names the columns that are labels rather than content — a label cell
     * gets the band colour and white text, so the reader can tell a title from a
     * value at a glance, the same treatment the change request's summary keeps (#228).
     */
    table(
      widths: number[],
      rows: string[][],
      opts: { head?: boolean; size?: number; labels?: number[] } = {}
    ) {
      const size = opts.size ?? 9.5;
      const pad = 6;
      const leading = size * 1.35;
      const cols = widths.map((w) => w * WIDTH);
      rows.forEach((row, at) => {
        const isHead = at === 0 && opts.head;
        const isLabel = (i: number) => !isHead && !!opts.labels?.includes(i);
        const wrapped = row.map((cell, i) =>
          wrap(cell, isHead || isLabel(i) ? bold : regular, size, cols[i] - pad * 2)
        );
        const height = Math.max(...wrapped.map((w) => w.length)) * leading + pad * 2 - 2;
        room(height);
        y -= height;
        let x = MARGIN;
        wrapped.forEach((cell, i) => {
          if (isHead) page.drawRectangle({ x, y, width: cols[i], height, color: SUNK });
          else if (isLabel(i)) page.drawRectangle({ x, y, width: cols[i], height, color: TINT_LABEL });
          page.drawRectangle({ x, y, width: cols[i], height, borderColor: RULE, borderWidth: 0.75 });
          cell.forEach((line, n) => {
            page.drawText(line, {
              x: x + pad,
              y: y + height - pad - size + 1 - n * leading,
              size,
              font: isHead || isLabel(i) ? bold : regular,
              color: isHead ? QUIET : isLabel(i) ? BRAND : INK,
            });
          });
          x += cols[i];
        });
      });
    },

    /** A rich-text field flattened to lines, each drawn with its marker and indent. */
    rich(doc: JSONContent | null | undefined, emptyNote: string) {
      const lines = richLines(doc);
      if (lines.length === 0) {
        api.text(emptyNote, { size: 9.5, colour: QUIET });
        return;
      }
      for (const line of lines) {
        api.text(line.text, { size: 9.5, indent: line.indent ?? 0, marker: line.marker, gap: 1 });
      }
    },
  };
  return api;
}

type Pen = ReturnType<typeof makePen>;

/** The same document produces the same bytes — pdf-lib stamps the save moment into
 *  every file, so the date is taken from the document's own prepared date, making
 *  the file a function of its content and nothing else. */
function stampDates(pdf: PDFDocument, preparedOn: string): void {
  const [day, month, year] = preparedOn.split('/').map(Number);
  const when =
    Number.isFinite(day) && Number.isFinite(month) && Number.isFinite(year)
      ? new Date(Date.UTC(year, month - 1, day))
      : new Date(Date.UTC(2000, 0, 1));
  pdf.setCreationDate(when);
  pdf.setModificationDate(when);
}

/** Every page carries the board, version and page n of m beneath a hairline, so a
 *  loose page still says which document it came from — the change request's rule. */
function stampFooters(pdf: PDFDocument, said: string, font: PDFFont): void {
  const pages = pdf.getPages();
  pages.forEach((page, at) => {
    const size = 7.5;
    page.drawLine({
      start: { x: MARGIN, y: MARGIN + 22 },
      end: { x: MARGIN + WIDTH, y: MARGIN + 22 },
      thickness: 0.75,
      color: RULE,
    });
    page.drawText(winAnsi(said), { x: MARGIN, y: MARGIN + 10, size, font, color: QUIET });
    const numbered = winAnsi(`Page ${at + 1} of ${pages.length}`);
    page.drawText(numbered, {
      x: MARGIN + WIDTH - font.widthOfTextAtSize(numbered, size),
      y: MARGIN + 10,
      size,
      font,
      color: QUIET,
    });
  });
}

/**
 * The requirements document, as bytes. Takes the composed document and a few facts
 * and lays them out; the caller has already worked out what it says.
 */
export async function requirementsPdf(
  facts: RequirementsFacts,
  model: RequirementsDoc
): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const titleFont = await embedTitleFont(pdf);

  pdf.setTitle(facts.title);
  pdf.setSubject(`Requirements — ${facts.board}`);
  pdf.setProducer('FlightDeck');
  stampDates(pdf, facts.preparedOn);

  const art = await embed(pdf, facts.logo);
  const pen = makePen(pdf, regular, bold, { title: facts.title, logo: art }, titleFont);
  pen.letterhead({
    eyebrow: 'Requirements document',
    subject: facts.title,
    meta: [revisionLabel(facts.version), ...preparedSegments(facts.preparedBy, facts.preparedOn), `Status: ${facts.status}`]
      .filter(Boolean)
      .join('  \u00b7  '),
    logo: art?.art,
  });

  const req = model.requirements;

  /* ── 1. Summary ───────────────────────────────────────────────────────────── */
  pen.band('Summary');
  // The revision is named only once the document has been re-issued; a first issue
  // carries no "Version 1" cell (a short row simply leaves the rest of its width
  // blank). See revisionLabel in pdf-chrome.
  const revised = Number(facts.version) > 1;
  pen.table(
    [0.22, 0.28, 0.22, 0.28],
    revised
      ? [
          ['Project', facts.board, 'Revision', String(facts.version)],
          ['Prepared', wordedFromSlashed(facts.preparedOn), 'Status', facts.status],
        ]
      : [
          ['Project', facts.board, 'Prepared', wordedFromSlashed(facts.preparedOn)],
          ['Status', facts.status],
        ],
    { labels: [0, 2] }
  );
  pen.down(2);
  const areaCount = req.areas.length;
  const summary =
    `${req.total} requirement${req.total === 1 ? '' : 's'}` +
    ` across ${areaCount} area${areaCount === 1 ? '' : 's'}` +
    (req.deferred.length ? `, ${req.deferred.length} held back` : '') +
    (req.counts.unset ? ` · ${req.counts.unset} not yet weighed for the release` : '');
  pen.text(summary, { size: 9.5, colour: QUIET });
  pen.down(4);

  /* ── 2. Purpose ───────────────────────────────────────────────────────────── */
  pen.band('Purpose');
  pen.rich(model.purpose, 'Not set in the charter yet.');
  if (richHasText(model.successMeasure)) {
    pen.down(4);
    pen.text('What success looks like', { size: 9.5, bold: true });
    pen.rich(model.successMeasure, 'Not set in the charter yet.');
  }
  pen.down(4);

  /* ── 3. Scope ─────────────────────────────────────────────────────────────── */
  pen.band('Scope');
  pen.text('In scope', { size: 9.5, bold: true });
  pen.rich(model.scopeIn, 'Not set in the charter yet.');
  pen.down(4);
  pen.text('Out of scope (this release)', { size: 9.5, bold: true });
  pen.rich(model.scopeOut, 'Not set in the charter yet.');
  pen.down(4);

  /* ── 4. Stakeholders ──────────────────────────────────────────────────────── */
  pen.band('Stakeholders');
  if (model.sponsor || model.stakeholders.length > 0) {
    if (model.sponsor) item(pen, model.sponsor, 'Sponsor');
    for (const s of model.stakeholders) item(pen, s.name, s.role?.trim() || '');
  } else {
    pen.text('No stakeholders named in the charter yet.', { size: 9.5, colour: QUIET });
  }
  pen.down(4);

  /* ── 5. Requirements ──────────────────────────────────────────────────────── */
  pen.band('Requirements');
  if (req.areas.length === 0) {
    pen.text('No requirements are in the project yet.', { size: 9.5, colour: QUIET });
  } else {
    req.areas.forEach((area, ai) => {
      pen.text(`${ai + 1}.  ${area.name}`, { size: 10, bold: true, colour: BRAND, gap: 2 });
      area.items.forEach((line) => {
        const label = line.moscow ? `  [${MOSCOW_SHORT[line.moscow]}]` : '';
        pen.text(`${line.title}${label}`, { size: 10.5, bold: true });
        if (line.summary) pen.text(line.summary, { size: 9, colour: QUIET });
        pen.down(3);
      });
      pen.down(2);
    });
  }
  pen.down(2);

  /* ── 6. Release necessity (the MoSCoW roll-up — named for the card field, not
        the separate Priority/urgency field) ──────────────────────────────────── */
  pen.band('Release necessity');
  pen.table(
    [0.2, 0.2, 0.2, 0.2, 0.2],
    [
      [...MOSCOW_ORDER.map((l) => MOSCOW_SHORT[l]), 'Not set'],
      [...MOSCOW_ORDER.map((l) => String(req.counts[l])), String(req.counts.unset)],
    ],
    { head: true }
  );
  pen.down(4);

  /* ── 7. Assumptions & constraints ─────────────────────────────────────────── */
  pen.band('Assumptions & constraints');
  pen.text('Assumptions', { size: 9.5, bold: true });
  pen.rich(model.assumptions, 'Not set in the charter yet.');
  pen.down(4);
  pen.text('Constraints', { size: 9.5, bold: true });
  pen.rich(model.constraints, 'Not set in the charter yet.');
  pen.down(4);

  /* ── 8. Out of scope & deferred ───────────────────────────────────────────── */
  pen.band('Out of scope & deferred');
  if (req.deferred.length === 0) {
    pen.text('Nothing is recorded as held back.', { size: 9.5, colour: QUIET });
  } else {
    for (const line of req.deferred) {
      pen.text(`${line.title}  [Deferred]`, { size: 10.5, bold: true });
      if (line.summary) pen.text(line.summary, { size: 9, colour: QUIET });
      if (line.why) pen.text(`Reason: ${line.why}`, { size: 9 });
      pen.down(3);
    }
  }

  stampFooters(
    pdf,
    [facts.board, revisionLabel(facts.version), `Prepared ${wordedFromSlashed(facts.preparedOn)}`]
      .filter(Boolean)
      .join('  \u00b7  '),
    regular
  );
  return pdf.save();
}

/** A stakeholder or requirement line: its title in bold, then its detail beneath. */
function item(pen: Pen, title: string, detail: string) {
  pen.text(title, { size: 10.5, bold: true });
  if (detail) pen.text(detail, { size: 9, colour: QUIET });
  pen.down(4);
}
